import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { client, fmtTimeAST } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronRight, Plus } from "lucide-react";
import { Toaster } from "sonner";
import { AddEquipmentDialog } from "@/components/AddEquipmentDialog";

export default function Equipment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdd = ["manager", "service_coordinator"].includes(user?.role);
  const [adding, setAdding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [equipment, setEquipment] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [e, a, s, c, j] = await Promise.all([
        client.get("/equipment"), client.get("/areas"),
        client.get("/sites"), client.get("/customers"), client.get("/jobs"),
      ]);
      setEquipment(e.data); setAreas(a.data); setSites(s.data); setCustomers(c.data); setJobs(j.data);
    })();
  }, [reloadKey]);

  const enriched = useMemo(() => equipment.map(eq => {
    const area = areas.find(a => a.id === eq.area_id);
    const site = sites.find(s => s.id === area?.site_id);
    const cust = customers.find(c => c.id === site?.customer_id);
    const cov = eq.coverage_end_date && new Date(eq.coverage_end_date) >= new Date();
    const history = jobs.filter(j => j.equipment_id === eq.id).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { ...eq, area, site, cust, cov, history };
  }), [equipment, areas, sites, customers, jobs]);

  const filtered = enriched.filter(e => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [e.make, e.model, e.serial_number, e.type, e.site?.name, e.area?.name, e.cust?.name]
      .filter(Boolean).some(x => x.toLowerCase().includes(s));
  });

  return (
    <div data-testid="equipment-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Assets</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Equipment Records</h1>
          <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>Click any asset for its full service timeline, parts fitted, and running cost.</div>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search make, model, serial, site…" value={q} onChange={e => setQ(e.target.value)}
            className="max-w-[320px] rounded-[10px]" data-testid="equipment-search" />
          {canAdd && (
            <Button className="bf-btn-primary px-4" onClick={() => setAdding(true)} data-testid="equipment-add-button">
              <Plus size={15} className="mr-2" /> Add equipment
            </Button>
          )}
        </div>
      </div>
      {canAdd && (
        <AddEquipmentDialog open={adding} onOpenChange={setAdding} onSaved={() => setReloadKey(k => k + 1)} />
      )}

      <div className="mt-6 bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }}>
        <table className="w-full text-[13.5px]">
          <thead>
            <tr style={{ background: "#F7F8FA" }}>
              <th className="th-condensed text-left px-3 py-2">Type / Make / Model</th>
              <th className="th-condensed text-left px-3 py-2">Serial</th>
              <th className="th-condensed text-left px-3 py-2">Location</th>
              <th className="th-condensed text-left px-3 py-2">Installed</th>
              <th className="th-condensed text-left px-3 py-2">Coverage</th>
              <th className="th-condensed text-left px-3 py-2">History</th>
              <th className="th-condensed text-left px-3 py-2 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} className="border-t hover:bg-[#F6F7F9] cursor-pointer"
                style={{ borderColor: "#EFF1F4" }}
                data-testid={`eq-row-${e.serial_number}`}
                onClick={() => navigate(`/equipment/${e.id}`)}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{e.make} {e.model}</div>
                  <div className="text-[11px]" style={{ color: "#5F6B7A" }}>{e.type} · {e.supplied_by_us ? "Supplied by us" : "Customer-owned"}</div>
                </td>
                <td className="px-3 py-2 mono">{e.serial_number}</td>
                <td className="px-3 py-2">
                  <div>{e.site?.name}</div>
                  <div className="text-[11px]" style={{ color: "#5F6B7A" }}>{e.cust?.name} · {e.area?.name}</div>
                </td>
                <td className="px-3 py-2 mono">{e.install_date}</td>
                <td className="px-3 py-2">
                  {e.coverage_type && e.coverage_type !== "None" ? (
                    <>
                      <span className="bf-chip" style={{ background: e.cov ? "#E9F7EE" : "#FDECEC", color: e.cov ? "#136B36" : "#B42318" }}>
                        <span className="bf-dot" style={{ background: e.cov ? "#136B36" : "#B42318" }} />
                        {e.cov ? "ACTIVE" : "EXPIRED"}
                      </span>
                      <div className="text-[12px] mt-1" style={{ color: "#5B6472" }}>{e.coverage_type} · {e.coverage_end_date}</div>
                    </>
                  ) : <span style={{ color: "#94A3B8" }}>—</span>}
                </td>
                <td className="px-3 py-2 mono text-[12px]">
                  {e.history.length} job{e.history.length !== 1 ? "s" : ""}
                  {e.history[0]?.completed_at && (
                    <div className="text-[11px]" style={{ color: "#5F6B7A" }}>last {fmtTimeAST(e.history[0].completed_at)}</div>
                  )}
                </td>
                <td className="px-3 py-2"><ChevronRight size={14} color="#94A3B8" /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[13px]" style={{ color: "#94A3B8" }}>No matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
